import React, { useState, useRef, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { StarIcon } from '@heroicons/react/24/solid'

const WhatsOnMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const dropdownRef = useRef(null)

  // Homepage categories only
  const popularCategories = [
    { name: 'All Categories', value: '', color: 'bg-gray-500' },
    {
      name: 'Classes & Workshops',
      value: 'classes & workshops',
      color: 'bg-workshops'
    },
    { name: 'Cinema', value: 'cinema', color: 'bg-film' },
    { name: 'Comedy', value: 'comedy', color: 'bg-comedy' },
    { name: 'Music', value: 'music', color: 'bg-music' },
    { name: 'Theatre', value: 'theatre', color: 'bg-theatre' },
    { name: 'Dance', value: 'dance', color: 'bg-dance' },
    { name: 'Family', value: 'family', color: 'bg-family' },
    { name: 'Visual Arts', value: 'visual arts', color: 'bg-visual' },
    { name: 'Event Cinema', value: 'event cinema', color: 'bg-film' }
  ]

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleCategoryClick = categoryValue => {
    // Build URL with category filter
    const url = new URL('/whatson', window.location.origin)
    if (categoryValue) {
      url.searchParams.append('categories[]', categoryValue)
    }
    window.location.href = url.toString()
  }

  return (
    <div className='relative' ref={dropdownRef}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center gap-2 px-2 sm:px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors'
        aria-expanded={isOpen}
        aria-haspopup='true'
      >
        <StarIcon className='w-5 h-5 text-yellow-500' />
        <span className='hidden sm:inline'>What's On</span>
      </button>

      {/* Mobile Fullscreen Menu */}
      {isMobile && isOpen && (
        <div className='fixed inset-0 z-50 bg-white dark:bg-gray-900'>
          {/* Header */}
          <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              What's On
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            >
              <XMarkIcon className='w-6 h-6' />
            </button>
          </div>

          <div className='overflow-y-auto h-[calc(100vh-4rem)]'>
            {/* Quick Links */}
            <div className='p-4'>
              <h4 className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider'>
                Quick Links
              </h4>
              <div className='space-y-2'>
                <a
                  href='/whatson?timeframe=today'
                  className='block px-4 py-3 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
                >
                  Today
                </a>
                <a
                  href='/whatson?timeframe=tomorrow'
                  className='block px-4 py-3 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
                >
                  Tomorrow
                </a>
                <a
                  href='/whatson?timeframe=this_week'
                  className='block px-4 py-3 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
                >
                  This Week
                </a>
                <a
                  href='/whatson?timeframe=this_month'
                  className='block px-4 py-3 text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
                >
                  This Month
                </a>
              </div>
            </div>

            {/* Categories */}
            <div className='p-4 border-t border-gray-200 dark:border-gray-700'>
              <h4 className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wider'>
                Browse by Category
              </h4>
              <div className='flex flex-wrap gap-3'>
                {popularCategories.map(category => (
                  <button
                    key={category.value}
                    onClick={() => handleCategoryClick(category.value)}
                    className={`
                      px-4 py-3 rounded-lg text-sm font-medium text-white transition-all whitespace-nowrap
                      ${category.color} hover:opacity-90
                    `}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* View All Link */}
            <div className='p-4 border-t border-gray-200 dark:border-gray-700'>
              <a
                href='/whatson'
                className='block w-full text-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium'
              >
                View All Events
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Dropdown Menu */}
      {!isMobile && isOpen && (
        <div className='absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50'>
          {/* Header */}
          <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              What's On
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            >
              <XMarkIcon className='w-5 h-5' />
            </button>
          </div>

          {/* Quick Links */}
          <div className='p-4'>
            <div className='space-y-2'>
              <a
                href='/whatson?timeframe=today'
                className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
              >
                Today
              </a>
              <a
                href='/whatson?timeframe=tomorrow'
                className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
              >
                Tomorrow
              </a>
              <a
                href='/whatson?timeframe=this_week'
                className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
              >
                This Week
              </a>
              <a
                href='/whatson?timeframe=this_month'
                className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
              >
                This Month
              </a>
            </div>
          </div>

          {/* Categories */}
          <div className='p-4 border-t border-gray-200 dark:border-gray-700'>
            <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
              Browse by Category
            </h4>
            <div className='flex flex-wrap gap-2'>
              {popularCategories.map(category => (
                <button
                  key={category.value}
                  onClick={() => handleCategoryClick(category.value)}
                  className={`
                    px-3 py-2 rounded-md text-sm font-medium text-white transition-all whitespace-nowrap
                    ${category.color} hover:opacity-90
                  `}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* View All Link */}
          <div className='p-4 border-t border-gray-200 dark:border-gray-700'>
            <a
              href='/whatson'
              className='block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium'
            >
              View All Events
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default WhatsOnMenu
